import React, { FC, ChangeEventHandler } from 'react'
import { ControllerProps } from 'react-hook-form'
import { ISelectProps } from 'transfix-ui/components/Select'
import ControlledSelect from '../ControlledSelect'
import { getStateOptions } from './utils'

type TBaseProps = {
  onChange: ChangeEventHandler<HTMLInputElement>
}

type IBaseControllerProps = ControllerProps<React.ComponentType<TBaseProps>>

export interface IControlledStateSelectProps
  extends Pick<IBaseControllerProps, 'control' | 'name' | 'defaultValue'> {
  SelectProps?: Partial<Omit<ISelectProps, 'options' | 'value' | 'onChange'>>
  /**
   * Filter out countries that are not needed
   * @default ['US', 'CA']
   */
  includeCountries?: string[]
  /**
   * Hides the group label for states, i.e. "US"
   * @default false
   */
  hideGroupLabel?: boolean
}

/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledStateSelect
 *   name='firstName'
 *   control={control}
 * />
 * ```
 */
const ControlledStateSelect: FC<IControlledStateSelectProps> = ({
  control,
  defaultValue,
  name,
  includeCountries = ['US', 'CA'],
  hideGroupLabel = false,
  SelectProps = {},
}) => {
  const options = getStateOptions(includeCountries, { hideGroupLabel })
  return (
    <ControlledSelect
      name={name}
      control={control}
      defaultValue={defaultValue}
      SelectProps={{
        label: 'State',
        options,
        ...SelectProps,
      }}
    />
  )
}

export default ControlledStateSelect
